import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Button, // Import Button component
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom"; // Import useNavigate

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import useLeaguesList from "../hooks/useLeaguesList";
import Event from "./Event";
import moment from "moment";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import { LoadingIndicator } from "./LoadingIndicator";
import { ErrorHandler } from "./ErrorHandler";
import { useBookmakers } from "../contexts/BookmakersContext";
import SettingsSection from "./SettingsSection";
import axios from "axios";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const EventsList = () => {
  const [selectedSport, setSelectedSport] = useState(() => {
    return localStorage.getItem("odds-comparison-sport");
  });
  const [expandedEvents, setExpandedEvents] = useState({});
  const [searchInput, setSearchInput] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [daysFilter, setDaysFilter] = useState("");
  const [hoursFilter, setHoursFilter] = useState("");

  const [favoritedLeagues, setFavoritedLeagues] = useState(() => {
    const storedFavorites = JSON.parse(localStorage.getItem("favoriteLeagues"));
    if (!!storedFavorites?.length > 0) {
      return storedFavorites;
    } else {
      const defaultLeagues = ["NRL", "AFL", "NBA", "English Premier League"];
      localStorage.setItem("favoriteLeagues", JSON.stringify(defaultLeagues));
      return defaultLeagues;
    }
  });

  const leagueRefs = useRef({});

  const toggleFavoriteLeague = (league) => {
    setFavoritedLeagues((prevFavorites) => {
      const isFavorite = prevFavorites.includes(league);
      const updatedFavorites = isFavorite
        ? prevFavorites.filter((fav) => fav !== league)
        : [...prevFavorites, league];
      localStorage.setItem("favoriteLeagues", JSON.stringify(updatedFavorites));
      return updatedFavorites;
    });
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded((prevExpanded) => ({
      ...prevExpanded,
      [panel]: !prevExpanded[panel],
    }));
  };

  const toggleEventExpansion = (eventId) => {
    setExpandedEvents((prevExpandedEvents) => ({
      ...prevExpandedEvents,
      [eventId]: !prevExpandedEvents[eventId],
    }));
  };

  const handleSportChange = (event) => {
    const newSport = event?.target?.value ?? null;
    setSelectedSport(newSport);
    if (newSport) {
      localStorage.setItem("odds-comparison-sport", newSport);
    } else {
      localStorage.removeItem("odds-comparison-sport");
    }
  };

  const handleLeagueClick = (league) => {
    if (leagueRefs.current[league]) {
      const topOffset =
        leagueRefs.current[league].getBoundingClientRect().top +
        window.pageYOffset -
        150;
      window.scrollTo({
        top: topOffset,
        behavior: "smooth",
      });

      setTimeout(() => {
        fetchEvents(league);
        setExpanded((prevExpanded) => ({
          ...prevExpanded,
          [league]: true,
        }));
      }, 700);
    }
  };

  const { bookmakers } = useBookmakers();

  const variants = {
    open: {
      opacity: 1,
      height: "auto",
      transition: {
        staggerChildren: 0.1,
        when: "beforeChildren",
        type: "spring",
        stiffness: 100,
        damping: 10,
      },
    },
    closed: { opacity: 0, height: 0 },
  };

  const {
    leagues,
    loading: leaguesLoading,
    error: leaguesError,
  } = useLeaguesList(selectedSport, searchInput);

  const [eventsByLeague, setEventsByLeague] = useState({});

  const sortedLeagues = useMemo(() => {
    const favoriteLeagues = leagues.filter((league) =>
      favoritedLeagues.includes(league)
    );
    const nonFavoriteLeagues = leagues
      .filter((league) => !favoritedLeagues.includes(league))
      .sort((a, b) => a.localeCompare(b));
    return ["Featured Events", ...favoriteLeagues, ...nonFavoriteLeagues];
  }, [leagues, favoritedLeagues]);

  useEffect(() => {
    const initialEventsByLeague = sortedLeagues.reduce((acc, league) => {
      acc[league] = {
        events: [],
        orderSubheadingMap: {},
        loading: false,
        error: null,
      };
      return acc;
    }, {});
    setEventsByLeague(initialEventsByLeague);

    if (sortedLeagues.length > 0) {
      if (searchInput) {
        sortedLeagues.forEach((league) => {
          fetchEvents(league);
          setExpanded((prevExpanded) => ({
            ...prevExpanded,
            [league]: true,
          }));
        });
      } else {
        fetchEvents(sortedLeagues[0]);
        setExpanded({ [sortedLeagues[0]]: true });
      }
    }
  }, [sortedLeagues, searchInput]);

  const fetchEvents = async (league) => {
    setEventsByLeague((prevEventsByLeague) => ({
      ...prevEventsByLeague,
      [league]: {
        ...prevEventsByLeague[league],
        loading: true,
      },
    }));

    if (league === "Featured Events") {
      fetchFeaturedEvents();
    } else {
      try {
        const matchesSearch = league
          .toLowerCase()
          .includes(searchInput.toLowerCase());
        const url =
          window.location.hostname === "localhost"
            ? "https://tool.bonusbank.com.au/api/v3/events/"
            : `${window.location.origin}/wp-json/bonusbank/v1/events`;

        var params = { sport: selectedSport || "", league: league };
        if (!!searchInput && !matchesSearch) {
          params = { search: searchInput, ...params };
        }

        const response = await axios.get(url, {
          params,
          headers: {
            "content-type": "application/json",
            "X-WP-Nonce": window.nonce,
          },
        });

        setEventsByLeague((prevEventsByLeague) => ({
          ...prevEventsByLeague,
          [league]: {
            ...prevEventsByLeague[league],
            events: response?.data?.events,
            orderSubheadingMap: response?.data?.order_subheading_map,
            error: null,
            loading: false,
          },
        }));
      } catch (err) {
        setEventsByLeague((prevEventsByLeague) => ({
          ...prevEventsByLeague,
          [league]: {
            ...prevEventsByLeague[league],
            error: err,
            events: [],
            loading: false,
          },
        }));
      }
    }
  };

  const fetchFeaturedEvents = async () => {
    const allFavoriteEventsPromises = favoritedLeagues.map(async (league) => {
      const url =
        window.location.hostname === "localhost"
          ? "https://tool.bonusbank.com.au/api/v3/events/"
          : `${window.location.origin}/wp-json/bonusbank/v1/events`;

      const params = { sport: selectedSport || "", league: league };

      const response = await axios.get(url, {
        params,
        headers: {
          "content-type": "application/json",
          "X-WP-Nonce": window.nonce,
        },
      });

      // Find the closest upcoming event
      const upcomingEvents = response?.data?.events?.filter((event) =>
        moment(event.datetime).isAfter(moment())
      );

      if (upcomingEvents && upcomingEvents.length > 0) {
        upcomingEvents.sort((a, b) =>
          moment(a.datetime).diff(moment(b.datetime))
        );
        return upcomingEvents[0];
      } else {
        return null;
      }
    });

    try {
      const allFavoriteEvents = await Promise.all(allFavoriteEventsPromises);
      const upcomingEvents = allFavoriteEvents
        .filter((event) => event !== null)
        .filter((event) => {
          if (!searchInput) return true;
          const lowerSearchInput = searchInput.toLowerCase();
          return (
            event.team1.toLowerCase().includes(lowerSearchInput) ||
            event.team2.toLowerCase().includes(lowerSearchInput) ||
            event.league.toLowerCase().includes(lowerSearchInput)
          );
        })
        .sort((a, b) => moment(a.datetime).diff(moment(b.datetime)));

      setEventsByLeague((prevEventsByLeague) => ({
        ...prevEventsByLeague,
        ["Featured Events"]: {
          ...prevEventsByLeague["Featured Events"],
          events: upcomingEvents,
          orderSubheadingMap: {},
          error: null,
          loading: false,
        },
      }));
    } catch (err) {
      setEventsByLeague((prevEventsByLeague) => ({
        ...prevEventsByLeague,
        ["Featured Events"]: {
          ...prevEventsByLeague["Featured Events"],
          error: err,
          events: [],
          loading: false,
        },
      }));
    }
  };

  const eventsByLeagueAndDate = useMemo(() => {
    return Object.entries(eventsByLeague).reduce((acc, [league, data]) => {
      if (!data || !data.events) {
        acc[league] = {};
        return acc;
      }
      const groupedEvents = data.events.reduce((acc, event) => {
        const date = moment(event.datetime).local().format("YYYY-MM-DD");
        if (!acc[date]) acc[date] = [];
        acc[date].push(event);
        return acc;
      }, {});

      Object.keys(groupedEvents).forEach((date) => {
        groupedEvents[date].sort((a, b) =>
          moment(a.datetime).diff(moment(b.datetime))
        );
      });

      const sortedDates = Object.keys(groupedEvents)
        .sort((a, b) => moment(a).diff(moment(b)))
        .reduce((newDatesObj, date) => {
          newDatesObj[date] = groupedEvents[date];
          return newDatesObj;
        }, {});

      acc[league] = sortedDates;
      return acc;
    }, {});
  }, [eventsByLeague]);

  const filteredEventsByLeagueAndDate = useMemo(() => {
    const now = moment();
    const maxDate = moment()
      .add(daysFilter || 0, "days")
      .add(hoursFilter || 0, "hours");

    return Object.entries(eventsByLeagueAndDate).reduce(
      (acc, [league, dates]) => {
        const filteredDates = Object.entries(dates).reduce(
          (datesAcc, [date, events]) => {
            const filteredEvents = events.filter((event) => {
              const eventDate = moment(event.datetime);
              if (selectedDate) {
                const startOfDay = moment(selectedDate).startOf("day");
                const endOfDay = moment(selectedDate).endOf("day");
                return eventDate.isBetween(startOfDay, endOfDay);
              }
              return (
                (!daysFilter && !hoursFilter) ||
                eventDate.isBetween(now, maxDate)
              );
            });

            if (filteredEvents.length) {
              datesAcc[date] = filteredEvents;
            }

            return datesAcc;
          },
          {}
        );

        if (Object.keys(filteredDates).length) {
          acc[league] = filteredDates;
        }

        return acc;
      },
      {}
    );
  }, [eventsByLeagueAndDate, daysFilter, hoursFilter, selectedDate]);

  const navigate = useNavigate(); // Use useNavigate hook here

  return (
    <Box
      sx={{
        display: { xs: "flex", lg: "grid" },
        flexDirection: "column",
        gridTemplateColumns: { lg: "300px auto 300px", xs: "1fr" },
        justifyContent: "center",
        alignItems: "start",
        width: "100%",
        gap: "16px",
        padding: { xs: 1, lg: 0 },
      }}
    >
      <SettingsSection
        selectedSport={selectedSport}
        handleSportChange={handleSportChange}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        daysFilter={daysFilter}
        setDaysFilter={setDaysFilter}
        hoursFilter={hoursFilter}
        setHoursFilter={setHoursFilter}
      />

      <Box
        sx={{
          marginTop: { xs: 0, lg: "16px" },
          padding: { xs: "24px 0px", lg: 0 },
          width: { xs: "100%", lg: "600px" },
          gridColumn: "2 / span 1",
          "& > *": {
            marginBottom: "16px",
          },
          order: { xs: 3, lg: 2 },
        }}
      >
        {leaguesLoading ? (
          <LoadingIndicator />
        ) : leaguesError ? (
          <ErrorHandler error={leaguesError} />
        ) : sortedLeagues.length > 0 ? (
          <Box>
            {sortedLeagues.map((league) => {
              if (
                league === "Featured Events" &&
                !eventsByLeague[league]?.events?.length &&
                !eventsByLeague[league]?.loading
              )
                return null;
              return (
                <Accordion
                  key={league}
                  expanded={expanded[league] || false}
                  onChange={handleAccordionChange(league)}
                  ref={(el) => (leagueRefs.current[league] = el)}
                  sx={{
                    border: "1px solid #f0f0f0",
                    boxShadow: "0px 16px 40px 0px #2C3D333D",
                    borderRadius: "16px !important",
                    mb: 2,
                    "&:before": {
                      display: "none",
                    },
                    "&.Mui-expanded": {
                      "& $summary": {
                        borderRadius: "16px 16px 0 0",
                      },
                    },
                    "&:not(.Mui-expanded)": {
                      "& $summary": {
                        borderRadius: "16px",
                      },
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                    className="summary"
                    onClick={() => {
                      if (!eventsByLeague[league].events.length) {
                        fetchEvents(league);
                      }
                    }}
                    sx={{
                      backgroundColor: "#0B2015",
                      color: "#fff",
                      height: "70px",
                      "&.Mui-expanded": {
                        height: "70px",
                        borderRadius: "16px 16px 0 0 !important",
                      },
                      "& > .MuiAccordionSummary-content": {
                        alignItems: "center",
                        margin: "12px 0",
                        "&.Mui-expanded": {
                          margin: "12px 0",
                        },
                      },
                    }}
                  >
                    <Typography variant="h5" sx={{ fontWeight: "600" }}>
                      {league}
                    </Typography>
                    {league === "Featured Events" && (
                      <Tooltip title="Featured Events shows the next up event for each of your favourite leagues">
                        <HelpOutlineIcon sx={{ color: "#fff", ml: 1 }} />
                      </Tooltip>
                    )}
                    {league !== "Featured Events" && (
                      <IconButton
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleFavoriteLeague(league);
                        }}
                        sx={{ marginLeft: "auto", color: "#fff" }}
                      >
                        {favoritedLeagues.includes(league) ? (
                          <StarIcon />
                        ) : (
                          <StarBorderIcon />
                        )}
                      </IconButton>
                    )}
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      padding: 0,
                      flexDirection: "column",
                    }}
                  >
                    {eventsByLeague[league]?.loading ? (
                      <LoadingIndicator />
                    ) : !eventsByLeague[league]?.events?.length ||
                      (eventsByLeague[league]?.events &&
                        Object.keys(filteredEventsByLeagueAndDate[league] || {})
                          .length === 0) ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          padding: "16px",
                        }}
                      >
                        <Typography variant="subTitle1">
                          We currently have no event data for this league.
                        </Typography>
                      </Box>
                    ) : (
                      Object.entries(
                        filteredEventsByLeagueAndDate[league] || {}
                      ).map(([date, events], index) => {
                        const formattedDate = moment(date)
                          .local()
                          .calendar(null, {
                            sameDay: "[Today]",
                            nextDay: "[Tomorrow]",
                            nextWeek: "dddd, MMMM Do",
                            sameElse: "dddd, MMMM Do",
                          });

                        return (
                          <AnimatePresence key={date}>
                            {expanded[league] && (
                              <motion.div
                                initial="closed"
                                animate="open"
                                exit="closed"
                                variants={variants}
                              >
                                <Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      backgroundColor: (theme) =>
                                        theme.palette.eventBackground.main,
                                      padding: "8px 16px",
                                    }}
                                  >
                                    <Typography variant="subTitle1">
                                      {formattedDate}
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "flex-end",
                                        width: { xs: "150px", lg: "240px" },
                                      }}
                                    >
                                      {Object.entries(
                                        eventsByLeague[league]
                                          .orderSubheadingMap
                                      ).map(([key, value]) => (
                                        <Typography
                                          key={key}
                                          variant="subTitle1"
                                          sx={{
                                            textAlign: "center",
                                            width: { xs: "50px", lg: "80px" },
                                          }}
                                        >
                                          {value}
                                        </Typography>
                                      ))}
                                    </Box>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: { xs: "1fr" },
                                    }}
                                  >
                                    {events.map((event) => (
                                      <React.Fragment key={event.eventId}>
                                        <motion.div
                                          initial={{ opacity: 0, y: 20 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          exit={{ opacity: 0, y: 20 }}
                                          transition={{
                                            type: "spring",
                                            stiffness: 100,
                                            damping: 10,
                                            delay: index * 0.05,
                                          }}
                                        >
                                          <div
                                            onClick={() =>
                                              toggleEventExpansion(
                                                event.eventId
                                              )
                                            }
                                          >
                                            <Event
                                              event={event}
                                              showLeague={
                                                league === "Featured Events"
                                              }
                                              onLeagueClick={handleLeagueClick}
                                            />
                                          </div>
                                        </motion.div>
                                      </React.Fragment>
                                    ))}
                                  </Box>
                                </Box>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        );
                      })
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        ) : (
          <Typography variant="h6" sx={{ textAlign: "center", mt: 4 }}>
            Sorry, we currently have no data for this sport.
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          order: { xs: 1, lg: 3 },
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <Button
          component={Link}
          to="/value"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 1,
            mb: 1,
            mt: "16px",
            p: 0,
            width: "max-content",
            textTransform: "none",
            textDecoration: "none",
            color: "inherit",
          }}
        >
          <Typography variant="h6" component="span">
            Value Bets
          </Typography>
          <ArrowForwardIcon sx={{ height: "16px", width: "16px" }} />
        </Button>
      </Box>
    </Box>
  );
};

export default EventsList;
